import type { TimeEntryItem, TimeReportData, TimeReportGroup } from '@hygge/shared';

/**
 * Flattening and paging for the grouped time report.
 *
 * The report arrives from the server as groups of entries, but the table renders
 * them as one flat run of rows: a banner for each group followed by that group's
 * entries. Paging therefore has to happen over the flattened run, not over the
 * groups — and a "page" counts ENTRIES only, so a page size of 50 always shows
 * 50 entries whether they fall in one group or ten.
 *
 * Nothing here is allowed to influence a total. Group totals, the report total
 * and everything the PDF prints are computed from the full report; this module
 * only decides which rows are visible on screen.
 */

export type ReportRow =
  /**
   * A group banner. `continued` is true when the page opens in the middle of a
   * group — the banner is repeated so the reader still knows whose rows these
   * are, and the caller can mark it "(continued)".
   */
  | { kind: 'group'; group: TimeReportGroup; continued: boolean }
  | { kind: 'entry'; entry: TimeEntryItem; group: TimeReportGroup };

/**
 * Page size normalisation. `null` means "no paging" — the "All" option, and
 * also the defensive reading of a zero, negative or non-finite size.
 */
function normalizePageSize(pageSize: number): number | null {
  if (!Number.isFinite(pageSize)) return null;
  const size = Math.floor(pageSize);
  return size > 0 ? size : null;
}

/**
 * The entry with this id as the report holds it now, or null.
 *
 * Show entries keeps the id of the entry its Edit dialog is open on, not the
 * object: a pencil clicked right after an inline save would otherwise hand
 * the dialog the row from before that save, and the dialog's Save would send
 * the old length back. [HW-974]
 */
export function findReportEntry(
  report: TimeReportData | undefined,
  id: string | null
): TimeEntryItem | null {
  if (!report || id === null) return null;
  for (const group of report.groups) {
    const found = group.entries.find((entry) => entry.id === id);
    if (found) return found;
  }
  return null;
}

/**
 * The cached report with a saved entry put in from the server's answer to the
 * save, so the row holds what was stored even when the refetch that follows
 * fails — a failed refetch keeps the old data, and an editor opened on it
 * would send the pre-save length back. The refetch, when it lands, replaces
 * all of this.
 *
 * The answer to a save carries no row flags (`canEdit` and the rest), and no
 * billable fields for a reader without "Edit any time log"; those are kept
 * from the cached row, except that an unadjusted billable follows the new
 * length, as the server's rule makes it. The group and report totals move by
 * the change in length. The entry stays in its group: a save that moved it to
 * another project, person or outside the window shows there until a refetch
 * lands. Unchanged (the same object) when the report does not hold the
 * entry. [HW-974]
 */
export function reportWithSavedEntry(
  report: TimeReportData | undefined,
  saved: TimeEntryItem
): TimeReportData | undefined {
  if (!report) return report;
  const cached = findReportEntry(report, saved.id);
  if (!cached) return report;
  const merged: TimeEntryItem = { ...cached, ...saved };
  if (saved.billableDuration === undefined && cached.billableDuration !== undefined) {
    merged.billableDuration =
      cached.billableAdjustedAt == null ? saved.duration : cached.billableDuration;
  }
  const delta = saved.duration - cached.duration;
  return {
    ...report,
    totalSeconds: report.totalSeconds + delta,
    groups: report.groups.map((group) =>
      group.entries.includes(cached)
        ? {
            ...group,
            totalSeconds: group.totalSeconds + delta,
            entries: group.entries.map((entry) => (entry === cached ? merged : entry)),
          }
        : group
    ),
  };
}

/**
 * The cached report without a deleted entry, its length out of the group and
 * report totals and a group left empty dropped, so a failed refetch after a
 * delete does not leave the row on screen for a second Delete. Unchanged
 * (the same object) when the report does not hold the entry. [HW-974]
 */
export function reportWithoutEntry(
  report: TimeReportData | undefined,
  entryId: string
): TimeReportData | undefined {
  if (!report) return report;
  const gone = findReportEntry(report, entryId);
  if (!gone) return report;
  return {
    ...report,
    totalSeconds: report.totalSeconds - gone.duration,
    groups: report.groups
      .map((group) =>
        group.entries.includes(gone)
          ? {
              ...group,
              totalSeconds: group.totalSeconds - gone.duration,
              entries: group.entries.filter((entry) => entry !== gone),
            }
          : group
      )
      .filter((group) => group.entries.length > 0),
  };
}

/** Total number of time entries in the report, across every group. */
export function countEntries(report: TimeReportData): number {
  let count = 0;
  for (const group of report.groups) count += group.entries.length;
  return count;
}

/**
 * Number of pages. Always at least 1 — an empty report is one empty page, not
 * zero pages, so the pager never renders "Page 1 of 0".
 */
export function pageCount(entryCount: number, pageSize: number): number {
  const size = normalizePageSize(pageSize);
  if (size === null) return 1;
  return Math.max(1, Math.ceil(entryCount / size));
}

/**
 * Every row of the report in display order: each group's banner, then its
 * entries.
 *
 * A group with no entries still yields its banner. The server builds groups out
 * of entries so this should not occur, but dropping the banner would silently
 * hide a group that the report claims to contain, and the banner carries the
 * group's own total.
 */
export function flattenReport(report: TimeReportData): ReportRow[] {
  const rows: ReportRow[] = [];
  for (const group of report.groups) {
    rows.push({ kind: 'group', group, continued: false });
    for (const entry of group.entries) {
      rows.push({ kind: 'entry', entry, group });
    }
  }
  return rows;
}

/**
 * The rows visible on `page` (1-based) at `pageSize` entries per page.
 *
 * Group banners do not consume a slot: a page holds `pageSize` entries plus
 * however many banners those entries need. A page that starts mid-group repeats
 * that group's banner with `continued: true`; one that starts exactly on a group
 * boundary gets `continued: false`.
 *
 * A page past the end (or below 1) returns [] rather than throwing — a stale
 * page number is expected, since a background refetch can shrink the report
 * under a reader who is on the last page.
 */
export function pageRows(report: TimeReportData, page: number, pageSize: number): ReportRow[] {
  const size = normalizePageSize(pageSize);
  const pages = pageCount(countEntries(report), pageSize);

  if (!Number.isFinite(page) || page < 1 || page > pages) return [];
  if (size === null) return flattenReport(report);

  const start = (page - 1) * size;
  const end = start + size;

  const rows: ReportRow[] = [];
  let seen = 0; // entries walked so far — an entry's ordinal across the whole report

  for (const group of report.groups) {
    if (group.entries.length === 0) {
      // No entry anchors this banner to a page, so put it where its position
      // falls, clamped so a trailing empty group cannot land past the last page.
      const emptyGroupPage = Math.min(Math.floor(seen / size), pages - 1);
      if (emptyGroupPage === page - 1) rows.push({ kind: 'group', group, continued: false });
      continue;
    }

    const groupStart = seen; // ordinal this group's first entry will take
    let bannerEmitted = false;

    for (const entry of group.entries) {
      const ordinal = seen++;
      if (ordinal < start || ordinal >= end) continue;
      if (!bannerEmitted) {
        rows.push({ kind: 'group', group, continued: ordinal !== groupStart });
        bannerEmitted = true;
      }
      rows.push({ kind: 'entry', entry, group });
    }
  }

  return rows;
}
